import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CATEGORY, SEVERITY, SEVERITY_ORDER } from '../compareFormat';
import { Notice } from '../ComparisonResult';

// The overview of what changed against the standard: how much needs review and how
// serious, what kind of changes, and which clauses they are in (worst first) — a
// clause opens its first finding in the file.

const REVIEW_LEVELS = ['high', 'medium', 'low', 'review'];

const Tile = ({ label, value, tone = 'tw-text-slate-900' }) => (
  <div className="tw-flex-1 tw-min-w-[96px] tw-px-3 tw-py-2 tw-rounded-lg tw-bg-slate-50">
    <p className="tw-m-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">{label}</p>
    <p className={`tw-m-0 tw-text-xl tw-font-bold tw-tabular-nums ${tone}`}>{value}</p>
  </div>
);
Tile.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node.isRequired, tone: PropTypes.string };

const worst = (findings) => SEVERITY_ORDER.find((s) => findings.some((f) => f.severity === s));

const byClause = (findings) => {
  const groups = findings.filter((f) => f.severity !== 'none').reduce((acc, f) => {
    const key = f.clause || '(no clause heading)';
    return { ...acc, [key]: [...(acc[key] || []), f] };
  }, {});
  return Object.entries(groups)
    .map(([clause, items]) => ({ clause, items, worst: worst(items) }))
    .sort((a, b) => SEVERITY_ORDER.indexOf(a.worst) - SEVERITY_ORDER.indexOf(b.worst) || b.items.length - a.items.length);
};

const ChangeSummary = ({ comparison, onSelect }) => {
  const s = comparison.stats || {};
  const findings = useMemo(() => comparison.findings || [], [comparison.findings]);
  const counts = s.by_severity || {};
  const toReview = REVIEW_LEVELS.reduce((n, k) => n + (counts[k] || 0), 0);
  const clauses = useMemo(() => byClause(findings), [findings]);
  const categories = Object.entries(s.by_category || {}).filter(([, n]) => n > 0);

  return (
    <section aria-label="Summary of changes" className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        <Tile label="To review" value={toReview} tone={toReview ? 'tw-text-red-700' : 'tw-text-emerald-700'} />
        <Tile label="High" value={counts.high || 0} tone={counts.high ? 'tw-text-red-700' : 'tw-text-slate-400'} />
        <Tile label="Fill-in / format" value={counts.none || 0} tone="tw-text-slate-500" />
        <Tile label="Same as standard" value={`${Math.round((s.similarity || 0) * 100)}%`} />
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
        {REVIEW_LEVELS.filter((k) => counts[k]).map((k) => (
          <span key={k} className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-border tw-border-solid tw-text-[12px] tw-font-semibold ${SEVERITY[k].cls}`}>
            {counts[k]} {SEVERITY[k].label.toLowerCase()}
          </span>
        ))}
        {categories.map(([key, n]) => (
          <span key={key} className="tw-text-[12px] tw-text-slate-500">· {n} {(CATEGORY[key] || key).toLowerCase()}</span>
        ))}
      </div>

      {s.wrong_template_suspected && (
        <Notice tone="amber">The files have little in common — is this the right standard (language / Contract vs Proposal)?</Notice>
      )}
      {comparison.ai_status === 'failed' && (
        <Notice tone="sky">AI review was not available, so differences are listed as &quot;to review&quot; without a verdict.</Notice>
      )}
      {s.scanned && <Notice tone="slate">The draft is a scan: its text was read by OCR, so a few differences may be reading errors.</Notice>}

      {clauses.length > 0 && (
        <div>
          <h3 className="tw-m-0 tw-mb-1 tw-text-[12px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">Changes by clause</h3>
          <ul className="tw-m-0 tw-p-0 tw-list-none tw-flex tw-flex-col">
            {clauses.map((c) => (
              <li key={c.clause}>
                <button type="button" onClick={() => onSelect(c.items[0])}
                  className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-rounded-md tw-bg-transparent tw-border-0 tw-text-left tw-cursor-pointer hover:tw-bg-slate-50">
                  <span className={`tw-w-2 tw-h-2 tw-rounded-full tw-shrink-0 tw-border tw-border-solid ${SEVERITY[c.worst]?.cls || ''}`} aria-hidden="true" />
                  <span className="tw-flex-1 tw-min-w-0 tw-truncate tw-text-[13px] tw-text-slate-700" title={c.clause}>{c.clause}</span>
                  <span className="tw-text-[12px] tw-tabular-nums tw-text-slate-500">{c.items.length}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

ChangeSummary.propTypes = { comparison: PropTypes.object.isRequired, onSelect: PropTypes.func.isRequired };

export default ChangeSummary;
